import { useState, useEffect, useMemo, useRef } from 'react';
import { X, RefreshCw, AlertTriangle } from 'lucide-react';
import type { ProjectoCampaign, ProjectoPLItem, ProjectoInvoice } from '../../types/projecto';
import { PROJECTO_STATUS_CONFIG } from '../../types/projecto';
import {
  projectoCampaignService,
  projectoPLService,
  projectoInvoiceService,
  computeProjectoPLSummary,
} from '../../services/projectoService';
import { getAgencyDefaultCommission } from '../../services/settingsService';
import { ProjectoGeneralTab } from './ProjectoGeneralTab';
import { ProjectoPLTab } from './ProjectoPLTab';
import { ProjectoInvoicesTab } from './ProjectoInvoicesTab';
import { ProjectoActivityTab } from './ProjectoActivityTab';
import { ProjectoSummaryBar } from './ProjectoSummaryBar';
import type { BillingTerms } from './ProjectoBillingBlock';
import { AdminOnlyToggle } from '../AdminOnlyToggle';
import { Button } from '../ui/Button';
import { log } from '../../lib/logger';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { reportFirstMissingField } from '../../utils/requiredFields';

type Tab = 'general' | 'pl' | 'invoices' | 'activity';

interface Props {
  project?: ProjectoCampaign | null;
  /** Coming from a notification about an invoice. */
  initialTab?: Tab;
  onClose: () => void;
  onSave: () => void;
}

const UNTITLED = 'Sem nome';

export function ProjectoForm({ project, initialTab, onClose, onSave }: Props) {
  const { toast, showConfirm } = useModal();
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const isNew = !project;
  const mountedRef = useRef(true);
  const autoCreateCalledRef = useRef(false);

  // An open project is mostly about its numbers; a new one starts with its name
  const [activeTab, setActiveTab] = useState<Tab>(initialTab ?? (isNew ? 'general' : 'pl'));
  const [formData, setFormData] = useState<Partial<ProjectoCampaign>>(
    project ?? {
      name: '',
      client_id: null,
      client_name: '',
      brand: '',
      reference: '',
      status: 'Draft',
      start_date: null,
      finish_date: null,
      notes: null,
      drive_link: null,
      budget_enabled: false,
      budget_amount: 0,
      target_margin_pct: 0,
      default_commission_pct: 0,
    }
  );
  const [items, setItems] = useState<ProjectoPLItem[]>([]);
  const [invoices, setInvoices] = useState<ProjectoInvoice[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedProjectoId, setSavedProjectoId] = useState<string | null>(project?.id ?? null);
  const [autoCreateError, setAutoCreateError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!project?.id) return;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      projectoPLService.getByProjectoId(project.id),
      projectoInvoiceService.getByProjectoId(project.id),
    ]).then(([plItems, inv]) => {
      if (!mountedRef.current) return;
      setItems(plItems);
      setInvoices(inv);
    }).catch(() => {
      if (!mountedRef.current) return;
      setLoadError('Não foi possível carregar o projeto. Tenta novamente.');
    }).finally(() => {
      if (mountedRef.current) setLoading(false);
    });
  }, [project?.id]);

  const summary = useMemo(() =>
    computeProjectoPLSummary(items, invoices, formData),
    [items, invoices, formData]
  );

  const terms: BillingTerms = {
    budget_enabled: !!formData.budget_enabled,
    budget_amount: Number(formData.budget_amount) || 0,
    target_margin_pct: Number(formData.target_margin_pct) || 0,
    default_commission_pct: Number(formData.default_commission_pct) || 0,
  };

  // ── Unsaved changes guard (P&L lines, billing terms and invoices save as you go) ──
  const serializeFormState = () => JSON.stringify({ ...formData, work_code: undefined });
  const dirtySnapshotRef = useRef<string | null>(null);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  useEffect(() => {
    dirtySnapshotRef.current = serializeFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotVersion]);
  const isDirty = () => dirtySnapshotRef.current !== null && dirtySnapshotRef.current !== serializeFormState();

  /** Saved at once, like the lines they govern; other unsaved edits stay unsaved. */
  const handleTermsChange = async (updates: Partial<BillingTerms>) => {
    setFormData((f) => ({ ...f, ...updates }));
    if (dirtySnapshotRef.current) {
      dirtySnapshotRef.current = JSON.stringify({ ...JSON.parse(dirtySnapshotRef.current), ...updates });
    }
    if (!savedProjectoId) return;
    try {
      await projectoCampaignService.update(savedProjectoId, updates);
      onSave();
    } catch (err) {
      log.error('[ProjectoForm] Failed to save billing terms:', err);
      setSaveError(err instanceof Error ? err.message : String(err));
    }
  };

  const requestClose = async () => {
    if (isDirty()) {
      const discard = await showConfirm({
        title: t('form.discardChanges.title'),
        message: t('form.discardChanges.message'),
        confirmText: t('common.discard'),
        cancelText: t('common.cancel'),
        variant: 'warning',
      });
      if (!discard) return;
    }
    // A new project exists from the moment the form opens; don't leave it behind if nothing was done
    if (isNew && savedProjectoId && !(formData.name ?? '').trim() && items.length === 0 && invoices.length === 0) {
      try {
        await projectoCampaignService.delete(savedProjectoId);
      } catch (err) {
        log.warn('[ProjectoForm] Empty project not removed:', err);
      }
      onSave();
    }
    onClose();
  };

  const handleSave = async () => {
    const missingRequired = reportFirstMissingField([
      {
        key: 'projecto-name',
        missing: !(formData.name ?? '').trim(),
        message: 'Preenche o Nome do projeto para poder guardar.',
        reveal: () => setActiveTab('general'),
      },
    ], toast.error);
    if (missingRequired) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (savedProjectoId) {
        await projectoCampaignService.update(savedProjectoId, formData);
      } else {
        const created = await projectoCampaignService.create({
          name: formData.name ?? '',
          client_id: formData.client_id,
          client_name: formData.client_name,
          brand: formData.brand,
          reference: formData.reference,
          status: formData.status,
          start_date: formData.start_date,
          finish_date: formData.finish_date,
          notes: formData.notes,
          drive_link: formData.drive_link,
          budget_enabled: formData.budget_enabled,
          budget_amount: formData.budget_amount,
          target_margin_pct: formData.target_margin_pct,
          default_commission_pct: formData.default_commission_pct,
          admin_only: formData.admin_only,
          account_id: formData.account_id,
        });
        setSavedProjectoId(created.id);
        setFormData((f) => ({ ...f, id: created.id }));
      }
      toast.success(t('projecto.saved'));
      setSnapshotVersion(v => v + 1);
      onSave();
    } catch (err) {
      // Surface the real cause instead of failing silently
      const message = err instanceof Error ? err.message : String(err);
      log.error('[ProjectoForm] Failed to save project:', err);
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const retryAutoCreate = async () => {
    setAutoCreateError(false);
    autoCreateCalledRef.current = false;
    doAutoCreate();
  };

  const doAutoCreate = async () => {
    if (autoCreateCalledRef.current) return;
    autoCreateCalledRef.current = true;
    try {
      const agencyCommission = Math.round((await getAgencyDefaultCommission()) * 10000) / 100;
      const created = await projectoCampaignService.create({
        name: UNTITLED,
        status: 'Draft',
        default_commission_pct: agencyCommission,
      });
      if (!mountedRef.current) return;
      setSavedProjectoId(created.id);
      setFormData((f) => ({ ...f, id: created.id, work_code: created.work_code, default_commission_pct: agencyCommission, account_id: created.account_id }));
      setSnapshotVersion(v => v + 1);
    } catch (err) {
      log.error('[ProjectoForm] Failed to initialize project:', err);
      if (mountedRef.current) {
        setAutoCreateError(true);
        setSaveError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  useEffect(() => {
    if (isNew && !savedProjectoId && !autoCreateCalledRef.current) {
      doAutoCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusCfg = PROJECTO_STATUS_CONFIG[formData.status ?? 'Draft'];
  const title = (formData.name ?? '').trim() && formData.name !== UNTITLED ? formData.name : (isNew ? 'Novo projeto' : UNTITLED);
  const showSummary = (activeTab === 'pl' || activeTab === 'invoices') && !!savedProjectoId && !loading && !loadError;

  const tabs: { id: Tab; label: string; badge?: number; warning?: boolean }[] = [
    { id: 'general', label: 'Geral' },
    { id: 'pl', label: 'P&L', badge: items.length || undefined, warning: summary.missingCostLines > 0 },
    { id: 'invoices', label: 'Faturas', badge: invoices.length || undefined },
    { id: 'activity', label: 'Atividade' },
  ];

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <p className="text-sm text-gray-600">{loadError}</p>
          <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
            Recarregar
          </Button>
        </div>
      );
    }

    if (!savedProjectoId && activeTab !== 'general') {
      if (autoCreateError) {
        return (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <p className="text-sm text-gray-600">Não foi possível preparar o projeto.</p>
            <Button variant="secondary" size="sm" onClick={retryAutoCreate}>
              Tentar novamente
            </Button>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          <span className="text-sm">A preparar o projeto...</span>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'general' && (
          <ProjectoGeneralTab
            data={formData}
            onChange={(u) => setFormData((f) => ({ ...f, ...u }))}
          />
        )}
        {activeTab === 'pl' && savedProjectoId && (
          <ProjectoPLTab
            projectoId={savedProjectoId}
            items={items}
            onItemsChange={setItems}
            terms={terms}
            summary={summary}
            onTermsChange={handleTermsChange}
          />
        )}
        {activeTab === 'invoices' && savedProjectoId && (
          <ProjectoInvoicesTab
            projectoId={savedProjectoId}
            invoices={invoices}
            onInvoicesChange={setInvoices}
            summary={summary}
            defaultClient={formData.client_name ?? ''}
            clientId={formData.client_id ?? null}
            projectLabel={[formData.work_code, formData.name].filter(Boolean).join(' · ') || 'Projeto'}
            reference={formData.reference ?? ''}
          />
        )}
        {activeTab === 'activity' && savedProjectoId && (
          <ProjectoActivityTab projectoId={savedProjectoId} />
        )}
      </div>
    );
  };

  return (
    <div className="cm-shell cm-shell--fullpage fixed inset-0 z-40 flex flex-col bg-white">
      <div className="cm-header relative !py-4 border-b border-gray-200">
        <div className="min-w-0 pr-12">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="cm-title truncate">{title}</h1>
            <span className={`cm-chip ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
            {formData.work_code && <span className="text-xs text-gray-500 font-mono">{formData.work_code}</span>}
          </div>
          {formData.client_name && (
            <p className="cm-subtitle truncate">{formData.client_name}{formData.brand ? ` · ${formData.brand}` : ''}</p>
          )}
          {isAdmin && (
            <div className="mt-1.5">
              <AdminOnlyToggle
                value={!!formData.admin_only}
                onChange={(next) => setFormData((f) => ({ ...f, admin_only: next }))}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={requestClose}
          className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="cm-tabbar flex-shrink-0 items-center px-2 sm:px-6 py-1.5 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`cm-tab max-sm:!px-3 ${activeTab === tab.id ? 'cm-tab--active' : ''}`}
          >
            {tab.label}
            {tab.badge != null && <span className="cm-tab-badge">{tab.badge}</span>}
            {tab.warning && <span aria-label="Precisa de atenção" className="w-2 h-2 rounded-full bg-amber-400" />}
          </button>
        ))}
      </nav>

      {showSummary && <ProjectoSummaryBar summary={summary} targetMarginPct={terms.target_margin_pct} />}

      {saveError && (
        <div className="flex items-start gap-2 px-6 py-3 bg-red-50 border-b border-red-200">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-800">Não foi possível guardar as alterações.</p>
            <p className="text-xs text-red-700 break-words">{saveError}</p>
          </div>
          <button
            type="button"
            onClick={() => setSaveError(null)}
            className="ml-auto text-red-400 hover:text-red-600 transition-colors"
            aria-label="Fechar aviso"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="cm-body flex-1 overflow-y-auto bg-gray-50">
        {renderTabContent()}
      </div>

      <div className="cm-footer !py-3">
        {activeTab === 'pl' || activeTab === 'invoices' ? (
          <p className="hidden sm:block mr-auto text-xs text-gray-500">As linhas e as faturas ficam guardadas ao adicionar.</p>
        ) : null}
        <Button variant="secondary" size="sm" onClick={requestClose}>
          {t('common.cancel', 'Cancelar')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          isLoading={saving}
          disabled={!(formData.name ?? '').trim()}
          onClick={handleSave}
        >
          {t('common.save', 'Guardar')}
        </Button>
      </div>
    </div>
  );
}
